/** The country picker's options, and what each one defaults to.
 *
 *  Kept in step with the API's data/countries.py by
 *  scripts/check-countries.mjs, which reads src/lib/countries.contract.json
 *  (generated from the Python) and fails if a code offered here is one the
 *  API does not know, or if the SELLABLE sets disagree. A country offered
 *  here that the API refuses is a 422 with no way forward for whoever picked
 *  it.
 */

export type MeasurementSystem = "imperial" | "metric";

export interface CountryOption {
  code: string;
  name: string;
  currency: string;
  locale: string;
  timezone: string;
  measurement: MeasurementSystem;
}

// Mirror of data/countries._ALL (code, name, currency, locale, timezone,
// measurement). Order = display order in the picker: sellable markets first.
export const COUNTRIES: CountryOption[] = [
  { code: "US", name: "United States", currency: "USD", locale: "en", timezone: "America/New_York", measurement: "imperial" },
  { code: "CA", name: "Canada", currency: "CAD", locale: "en", timezone: "America/Toronto", measurement: "imperial" },
  { code: "GB", name: "United Kingdom", currency: "GBP", locale: "en", timezone: "Europe/London", measurement: "imperial" },
  { code: "IE", name: "Ireland", currency: "EUR", locale: "en", timezone: "Europe/Dublin", measurement: "metric" },
  { code: "AU", name: "Australia", currency: "AUD", locale: "en", timezone: "Australia/Sydney", measurement: "metric" },
  { code: "NZ", name: "New Zealand", currency: "NZD", locale: "en", timezone: "Pacific/Auckland", measurement: "metric" },
  { code: "DE", name: "Germany", currency: "EUR", locale: "en", timezone: "Europe/Berlin", measurement: "metric" },
  { code: "FR", name: "France", currency: "EUR", locale: "fr", timezone: "Europe/Paris", measurement: "metric" },
  { code: "NL", name: "Netherlands", currency: "EUR", locale: "en", timezone: "Europe/Amsterdam", measurement: "metric" },
  { code: "ES", name: "Spain", currency: "EUR", locale: "es", timezone: "Europe/Madrid", measurement: "metric" },
];

// Where we can complete a NestPlot subscription sale. Mirrors countries.SELLABLE.
export const SELLABLE = new Set(["US", "CA", "GB", "IE", "AU", "NZ"]);

// The currencies an operator can collect rent in (the picker's options). A
// superset of what we price our own subscription in.
export const CURRENCIES = ["USD", "CAD", "GBP", "EUR", "AUD", "NZD"] as const;

// A representative timezone list spanning the sellable markets, for the
// region picker's time-zone select. Not exhaustive — the field also accepts a
// typed IANA zone for anything not listed.
export const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Australia/Sydney",
  "Australia/Perth",
  "Pacific/Auckland",
];

export function countryByCode(code: string | null | undefined): CountryOption | undefined {
  return COUNTRIES.find((c) => c.code === (code || "").toUpperCase());
}
